<template>
  <!--
    NO `data-aos` HERE. It looks harmless and it broke the mobile menu outright.

    AOS applies its resting state as `[data-aos="fade-down"] { transform:
    translate3d(0,-100px,0) }` and only cancels it by adding an `aos-animate`
    CLASS imperatively, from outside Vue. This element also binds `:class`, so
    the next time `mobileOpen` flipped, Vue re-rendered the class attribute from
    its own array and wiped `aos-init aos-animate` along with it -- measured:
    "nav aos-init aos-animate" before the tap, "nav nav--menu-open" after. The
    header then snapped back to its -100px resting state and flew off screen.

    That transform also made the header a CONTAINING BLOCK for `position: fixed`
    descendants, which is what collapsed .drawer to 0px tall (see .drawer CSS).

    A navbar that is `position: fixed` at the top of the viewport has nothing to
    scroll-reveal anyway.
  -->
  <header
    id="navigation"
    :class="['nav', { 'nav--scrolled': scrolled, 'nav--menu-open': mobileOpen }]"
  >
    <div class="nav__inner">
      <!-- Brand -->
      <router-link to="/" class="nav__brand" :aria-label="$t('siteUi.aboodHome')">
        <img src="@/assets/images/abood_icons.svg" alt="" />
      </router-link>

      <!-- Primary links — desktop -->
      <nav class="nav__primary" :aria-label="$t('siteUi.primary')">
        <a
          v-for="link in primaryLinks"
          :key="link.id || link.path"
          :href="link.id ? `#${link.id}` : undefined"
          :class="['nav__link', { 'nav__link--active': isActive(link) }]"
          @click="handleLinkClick($event, link)"
        >
          {{ $t(link.labelKey) }}
        </a>
      </nav>

      <!-- Right cluster -->
      <div class="nav__right">
        <!--
          MANAGE AN EXISTING TICKET.

          This lived only in the footer, which measured at 94% down the page —
          5.3 screens of scrolling on a 1440x1000 desktop. Somebody who has to
          cancel a trip is in a hurry and looks at the top of the page, so a
          link they can only reach by scrolling past the entire marketing site
          may as well not exist.

          Deliberately NOT in .nav__primary next door: those five are in-page
          section anchors for browsing. This is a utility action on a booking
          you already hold, which is what this right-hand cluster is for — it
          sits beside the language picker and sign-in for the same reason.
        -->
        <router-link to="/change-ticket" class="nav__manage">
          <Icon icon="mdi:calendar-edit" />
          <span>{{ $t("siteUi.manageTicketShort") }}</span>
        </router-link>

        <LanguageSwitch class="nav__lang" />

        <button
          v-if="!isLoggedIn"
          type="button"
          class="nav__login"
          @click="$emit('show-login')"
        >
          <Icon icon="mdi:login" />
          <span>{{ $t("navbar.login") }}</span>
        </button>

        <div v-else class="nav__user" v-click-outside="closeUserMenu">
          <button
            type="button"
            class="nav__user-btn"
            @click="userOpen = !userOpen"
            :aria-expanded="userOpen.toString()"
            aria-haspopup="menu"
          >
            <img
              :src="currentUser?.user_image_url || defaultAvatar"
              :alt="currentUser?.name || 'Account'"
              @error="onAvatarError"
            />
          </button>
          <transition name="menu">
            <ul v-if="userOpen" class="nav__user-menu" role="menu">
              <li>
                <router-link to="/user-profile" @click="userOpen = false">
                  <Icon icon="mdi:account-outline" />
                  {{ currentUser?.name || $t("navbar.profile", "Profile") }}
                </router-link>
              </li>
              <li>
                <router-link to="/ticket" @click="userOpen = false">
                  <Icon icon="mdi:ticket-confirmation-outline" />
                  {{ $t("navbar.tickets", "My tickets") }}
                </router-link>
              </li>
              <li class="nav__user-divider" />
              <li>
                <button type="button" class="nav__user-logout" @click="handleLogout">
                  <Icon icon="mdi:logout" />
                  {{ $t("navbar.logout", "Logout") }}
                </button>
              </li>
            </ul>
          </transition>
        </div>

        <!-- Hamburger — only renders below the tablet breakpoint -->
        <button
          type="button"
          class="nav__burger"
          :aria-expanded="mobileOpen.toString()"
          aria-controls="nav-drawer"
          :aria-label="$t('siteUi.toggleMenu')"
          @click="mobileOpen = !mobileOpen"
        >
          <Icon :icon="mobileOpen ? 'mdi:close' : 'mdi:menu'" />
        </button>
      </div>
    </div>

    <!-- ── Mobile drawer ─────────────────────────────────────────── -->
    <!--
      Teleported to <body> ON PURPOSE.

      As a DOM child of <header class="nav"> the drawer was NOT viewport-fixed:
      the header carries `backdrop-filter: blur(14px)`, and a backdrop-filter
      (like transform and filter) makes an element the containing block for its
      `position: fixed` descendants. So `top: 80px; bottom: 0` resolved against
      the HEADER box, not the screen — and the header is 70px tall on phones
      (`#navigation { height: 70px !important }` at <=540px in main.css), giving
      the drawer a height of 70 - 80 = -10, clamped to 0. Measured: 390x0 at
      y=-20. The menu "opened" to nothing while body scroll stayed locked.

      Teleporting makes `position: fixed` resolve against the viewport again and
      keeps it that way no matter what filters or transforms the header grows
      later.
    -->
    <Teleport to="body">
    <transition name="drawer">
      <div
        v-if="mobileOpen"
        id="nav-drawer"
        class="drawer"
        role="dialog"
        aria-modal="true"
        :aria-label="$t('siteUi.siteMenu')"
        @click.self="mobileOpen = false"
      >
        <nav class="drawer__nav" :aria-label="$t('siteUi.primaryMobile')">
          <a
            v-for="link in primaryLinks"
            :key="link.id || link.path"
            :href="link.id ? `#${link.id}` : undefined"
            :class="['drawer__link', { 'drawer__link--active': isActive(link) }]"
            @click="handleLinkClick($event, link, true)"
          >
            <Icon :icon="link.icon" />
            {{ $t(link.labelKey) }}
          </a>
          <!-- Not in the desktop nav: that bar is for browsing the service.
               Here it sits with the other links, where somebody already looking
               for help will find it. -->
          <router-link to="/change-ticket" class="drawer__link" @click="mobileOpen = false">
            <Icon icon="mdi:calendar-edit" />
            {{ $t('siteUi.guestAmendTitle') }}
          </router-link>
        </nav>

        <div class="drawer__foot">
          <button
            v-if="!isLoggedIn"
            type="button"
            class="drawer__login"
            @click="onLoginClick"
          >
            <Icon icon="mdi:login" />
            {{ $t("navbar.login") }}
          </button>
          <a
            href="https://wa.me/255793444333"
            target="_blank"
            rel="noopener"
            class="drawer__cta"
          >
            <Icon icon="mdi:whatsapp" />
            +255 793 444 333
          </a>
        </div>
      </div>
    </transition>
    </Teleport>
  </header>
</template>

<script>
import { computed } from "vue";
import { useStore } from "vuex";
import LanguageSwitch from "./LanguageSwitch.vue";
import { Icon } from "@iconify/vue";
import { notify } from "@/plugins/toast";
import identity from "@/services/identity";

// v2 identity contract; see src/services/identity.js.
const axiosInstance = identity;

// Click-outside directive — used by the user-menu dropdown so taps
// elsewhere on the page close it without a global mousedown listener.
const clickOutside = {
  beforeMount(el, binding) {
    el.__clickOutsideHandler__ = (event) => {
      if (!(el === event.target || el.contains(event.target))) {
        binding.value(event);
      }
    };
    document.addEventListener("mousedown", el.__clickOutsideHandler__);
  },
  unmounted(el) {
    document.removeEventListener("mousedown", el.__clickOutsideHandler__);
  },
};

export default {
  name: "AppNavbar",
  components: { LanguageSwitch, Icon },
  directives: { "click-outside": clickOutside },
  setup() {
    const store = useStore();
    return {
      isLoggedIn:  computed(() => store.getters.isAuthenticated),
      currentUser: computed(() => store.getters.currentUser),
      authToken:   computed(() => store.getters.authToken),
    };
  },
  data() {
    return {
      // Anchor map — single source of truth for what the menu offers
      // and which section it lights up against. `icon` shows on the
      // mobile drawer; desktop renders text only. The labelKey is the
      // existing i18n path (no translation file edits needed).
      primaryLinks: [
        { id: "home",     path: "/", labelKey: "navbar.home",     icon: "mdi:home-outline" },
        { id: "about-us",          labelKey: "navbar.about",    icon: "mdi:information-outline" },
        { id: "our-trip",          labelKey: "navbar.offices",  icon: "mdi:office-building-marker-outline" },
        { id: "routes",            labelKey: "navbar.routes",   icon: "mdi:map-marker-path" },
        { id: "contact",           labelKey: "navbar.contact",  icon: "mdi:phone-outline" },
      ],
      activeId: "home",
      scrolled: false,
      mobileOpen: false,
      userOpen: false,
      observer: null,
      defaultAvatar:
        "data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 32 32'><rect width='32' height='32' rx='16' fill='%23E8F1FB'/><text x='50%' y='55%' text-anchor='middle' font-size='14' font-family='Inter, sans-serif' fill='%231158ab' font-weight='700'>A</text></svg>",
    };
  },
  watch: {
    // Lock body scroll while the mobile drawer is open
    mobileOpen(open) {
      document.body.style.overflow = open ? "hidden" : "";
    },
    $route() {
      this.mobileOpen = false;
      this.userOpen = false;
    },
  },
  mounted() {
    this.onScroll();
    window.addEventListener("scroll", this.onScroll, { passive: true });
    this.setupActiveObserver();
  },
  beforeUnmount() {
    window.removeEventListener("scroll", this.onScroll);
    document.body.style.overflow = "";
    if (this.observer) this.observer.disconnect();
  },
  methods: {
    onScroll() {
      this.scrolled = window.scrollY > 8;
    },
    isActive(link) {
      return this.activeId === link.id;
    },
    /**
     * Smooth-scroll to the target section when on the home page,
     * otherwise navigate to `/#section` so the anchor lands after
     * route change.
     */
    handleLinkClick(event, link, closeDrawer = false) {
      if (closeDrawer) this.mobileOpen = false;
      if (!link.id) return;
      if (link.id === "home") {
        event.preventDefault();
        if (this.$route.path !== "/") this.$router.push("/");
        window.scrollTo({ top: 0, behavior: "smooth" });
        this.activeId = "home";
        return;
      }
      if (this.$route.path !== "/") {
        event.preventDefault();
        this.$router.push(`/#${link.id}`);
        return;
      }
      const el = document.getElementById(link.id);
      if (el) {
        event.preventDefault();
        const top = el.getBoundingClientRect().top + window.scrollY - 70;
        window.scrollTo({ top, behavior: "smooth" });
        this.activeId = link.id;
      }
    },
    /**
     * Track which section is in view and light up the matching link.
     * Observes elements by id with a generous rootMargin so the
     * underline updates near the centre of the viewport, not the top.
     */
    setupActiveObserver() {
      if (!("IntersectionObserver" in window)) return;
      this.observer = new IntersectionObserver(
        (entries) => {
          for (const e of entries) {
            if (e.isIntersecting) {
              this.activeId = e.target.id;
              break;
            }
          }
        },
        { rootMargin: "-40% 0px -55% 0px", threshold: 0 }
      );
      for (const link of this.primaryLinks) {
        if (!link.id) continue;
        const el = document.getElementById(link.id);
        if (el) this.observer.observe(el);
      }
      // Pseudo-section for the very top of the page → "home" stays
      // active when scrolled near top.
      window.addEventListener("scroll", () => {
        if (window.scrollY < 240) this.activeId = "home";
      }, { passive: true });
    },
    closeUserMenu() {
      this.userOpen = false;
    },
    onLoginClick() {
      this.mobileOpen = false;
      this.$emit("show-login");
    },
    onAvatarError(event) {
      event.target.src = this.defaultAvatar;
    },
    async handleLogout() {
      this.userOpen = false;
      try {
        await axiosInstance.post(
          "auth/logout",
          {},
          { headers: { Authorization: `Bearer ${this.authToken}` } }
        );
        this.$store.dispatch("logout");
        this.$router.push("/");
        notify.success(this.$t("navbar.logged_out", "Signed out."));
      } catch (err) {
        notify.error(this.$t("navbar.logout_failed", "Couldn't sign out. Try again."));
      }
    },
  },
};
</script>

<style scoped>
/* ── Header bar ─────────────────────────────────────────────── */
.nav {
  position: fixed;
  top: 0; left: 0; right: 0;
  /* Pinned to 80px to match the legacy `#navigation { height: 80px
   * !important }` rule in main.css AND the hero's `margin-top: -80px`
   * offset that pulls the dark band behind the navbar. Drop both
   * together if you ever resize. */
  height: 80px;
  background: rgba(255, 255, 255, 0.85);
  backdrop-filter: blur(14px) saturate(160%);
  -webkit-backdrop-filter: blur(14px) saturate(160%);
  border-bottom: 1px solid transparent;
  z-index: 999;
  transition: background .2s ease, border-color .2s ease, box-shadow .2s ease;
}
.nav--scrolled {
  background: rgba(255, 255, 255, 0.96);
  border-bottom-color: var(--color-border, #e2e8f0);
  box-shadow: 0 6px 24px rgba(8, 23, 49, 0.06);
}
.nav__inner {
  width: 100%;
  height: 100%;
  margin: 0 auto;
  padding: 0 32px;
  display: flex;
  align-items: center;
  gap: 24px;
}

/* Brand */
.nav__brand {
  flex: 0 0 auto;
  display: inline-flex; align-items: center;
}
.nav__brand img {
  height: 36px;
  width: auto;
  display: block;
}

/* Primary links — desktop only */
.nav__primary {
  flex: 1 1 auto;
  display: flex; align-items: center; justify-content: center;
  gap: 4px;
}
.nav__link {
  position: relative;
  padding: 8px 14px;
  font: 700 13.5px/1 var(--font-sans, sans-serif);
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: var(--color-text-2, #334155);
  text-decoration: none;
  border-radius: 8px;
  transition: color .15s, background .15s;
}
.nav__link:hover { color: var(--color-text, #0f172a); background: var(--color-surface-2, #f8fafc); }
.nav__link--active {
  color: var(--color-red, #e11d48);
}
.nav__link--active::after {
  content: "";
  position: absolute;
  left: 14px; right: 14px;
  bottom: -2px;
  height: 3px;
  background: var(--color-red, #e11d48);
  border-radius: 2px;
}

/* Right cluster */
.nav__right {
  flex: 0 0 auto;
  /*
   * Pin to the right gutter.
   *
   * .nav__inner is a plain flex row with no `justify-content`, so it relies on
   * `.nav__primary { flex: 1 1 auto }` swallowing the free space to push this
   * group right. That spacer is `display: none` below 980px, so on a phone the
   * burger simply sat one `gap` after the brand -- measured 94px short of the
   * right edge at 390px, instead of the 16px gutter.
   *
   * `auto` resolves to 0 on desktop, where .nav__primary has already eaten the
   * free space, so this is mobile-only in effect without a media query.
   */
  margin-left: auto;
  display: flex; align-items: center; gap: 10px;
}
.nav__manage {
  display: inline-flex; align-items: center; gap: 6px;
  padding: 8px 12px;
  border-radius: var(--radius-pill, 999px);
  color: var(--color-text-2, #475569);
  font: 700 12.5px/1 var(--font-sans, sans-serif);
  text-decoration: none;
  white-space: nowrap;
  transition: background-color .15s ease, color .15s ease;
}
.nav__manage:hover { background: var(--color-bg, #f1f5f9); color: var(--color-text, #0b1224); }
.nav__manage:focus-visible { outline: 2px solid var(--color-primary, #1158ab); outline-offset: 2px; }
.nav__manage svg { font-size: 16px; }

/* Below this the navbar is already tight with the flag and sign-in; the
   drawer carries the same link, so nothing is lost. */
@media (max-width: 1100px) { .nav__manage { display: none; } }

.nav__lang { display: inline-flex; }

/* Login button — brand-red CTA */
.nav__login {
  display: inline-flex; align-items: center; gap: 7px;
  padding: 9px 14px;
  border: 0;
  background: linear-gradient(135deg, var(--color-red, #e11d48) 0%, var(--color-red-600, #be123c) 100%);
  color: #fff;
  font: 700 12.5px/1 var(--font-sans, sans-serif);
  text-transform: uppercase;
  letter-spacing: 0.06em;
  border-radius: 999px;
  cursor: pointer;
  /*
   * A SMALLER resting shadow than before (6px/16px -> 3px/10px).
   *
   * The old pair jumped from a 16px blur to a 20px blur with a 4px offset
   * change on hover, which reads as the button lurching rather than
   * responding. The shadow now grows by a little and the button lifts by 1px,
   * so the feedback is legible without being a movement you have to track.
   */
  box-shadow: 0 3px 10px rgba(225, 29, 72, 0.3);
  transition: transform .12s ease, box-shadow .18s ease, filter .18s ease;
}
.nav__login:hover {
  transform: translateY(-1px);
  box-shadow: 0 6px 16px rgba(225, 29, 72, 0.38);
  filter: brightness(1.05);
}
.nav__login:active {
  transform: translateY(0);
  box-shadow: 0 2px 6px rgba(225, 29, 72, 0.3);
}
/*
 * There was no focus style at all — a keyboard user tabbing to the primary
 * action on the site had nothing telling them they had arrived. focus-visible
 * so it does not fire on mouse clicks.
 */
.nav__login:focus-visible {
  outline: 2px solid var(--color-primary, #1158ab);
  outline-offset: 3px;
}
.nav__login svg { font-size: 16px; }

@media (prefers-reduced-motion: reduce) {
  .nav__login { transition: box-shadow .18s ease; }
  .nav__login:hover, .nav__login:active { transform: none; }
}

/* User menu */
.nav__user { position: relative; }
.nav__user-btn {
  width: 38px; height: 38px;
  border: 1px solid var(--color-border, #e2e8f0);
  background: var(--color-surface, #fff);
  border-radius: 50%;
  padding: 0;
  cursor: pointer;
  overflow: hidden;
  transition: border-color .15s, box-shadow .15s;
}
.nav__user-btn:hover { border-color: var(--color-red, #e11d48); box-shadow: 0 0 0 3px rgba(225, 29, 72, 0.15); }
.nav__user-btn img { width: 100%; height: 100%; object-fit: cover; display: block; }
.nav__user-menu {
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  min-width: 220px;
  margin: 0; padding: 6px;
  list-style: none;
  background: var(--color-surface, #fff);
  border: 1px solid var(--color-border, #e2e8f0);
  border-radius: 12px;
  box-shadow: 0 18px 38px rgba(8, 23, 49, 0.14);
}
.nav__user-menu a,
.nav__user-menu button {
  display: flex; align-items: center; gap: 10px;
  width: 100%;
  padding: 10px 12px;
  border: 0; background: transparent;
  font: 600 13.5px/1 var(--font-sans, sans-serif);
  color: var(--color-text, #0f172a);
  text-decoration: none;
  border-radius: 8px;
  cursor: pointer;
  text-align: left;
}
.nav__user-menu a:hover,
.nav__user-menu button:hover { background: var(--color-surface-2, #f8fafc); }
.nav__user-menu svg { font-size: 18px; color: var(--color-muted, #64748b); }
.nav__user-divider {
  height: 1px;
  background: var(--color-border, #e2e8f0);
  margin: 4px 0;
}
.nav__user-logout { color: var(--color-red, #e11d48); }
.nav__user-logout svg { color: var(--color-red, #e11d48); }

/* Hamburger */
.nav__burger {
  display: none;
  width: 38px; height: 38px;
  align-items: center; justify-content: center;
  border: 0; background: transparent;
  color: var(--color-text, #0f172a);
  cursor: pointer;
  font-size: 24px;
  border-radius: 8px;
}
.nav__burger:hover { background: var(--color-surface-2, #f8fafc); }

/* Menu transitions */
.menu-enter-active, .menu-leave-active { transition: opacity .15s, transform .15s; }
.menu-enter-from, .menu-leave-to { opacity: 0; transform: translateY(-4px); }

/* ── Mobile drawer ─────────────────────────────────────────── */
.drawer {
  /*
   * `top` must equal the REAL navbar height or the drawer either overlaps the
   * header or floats below it. main.css forces `#navigation { height: 70px
   * !important }` at <=540px, so 80px is wrong on exactly the screens the
   * drawer exists for -- it left a 10px seam. Kept in one variable so the two
   * cannot drift apart again.
   */
  --drawer-top: 80px;
  position: fixed;
  top: var(--drawer-top); left: 0; right: 0; bottom: 0;
  background: rgba(8, 12, 28, 0.5);
  z-index: 998;
  display: flex; flex-direction: column;
  pointer-events: auto;
}
.drawer__nav {
  background: var(--color-surface, #fff);
  border-top: 1px solid var(--color-border, #e2e8f0);
  border-bottom: 1px solid var(--color-border, #e2e8f0);
  padding: 8px;
}
.drawer__link {
  display: flex; align-items: center; gap: 14px;
  padding: 14px 16px;
  text-decoration: none;
  color: var(--color-text, #0f172a);
  font: 700 14px/1 var(--font-sans, sans-serif);
  border-radius: 10px;
}
.drawer__link svg { font-size: 22px; color: var(--color-muted, #64748b); }
.drawer__link:hover { background: var(--color-surface-2, #f8fafc); }
.drawer__link--active {
  color: var(--color-red, #e11d48);
  background: rgba(225, 29, 72, 0.06);
}
.drawer__link--active svg { color: var(--color-red, #e11d48); }
.drawer__foot {
  background: var(--color-surface, #fff);
  border-bottom: 1px solid var(--color-border, #e2e8f0);
  padding: 14px 16px;
  display: flex; flex-direction: column; gap: 10px;
}
.drawer__login {
  display: inline-flex; align-items: center; justify-content: center; gap: 8px;
  padding: 12px 16px;
  border: 0;
  border-radius: 10px;
  background: linear-gradient(135deg, var(--color-red, #e11d48) 0%, var(--color-red-600, #be123c) 100%);
  color: #fff;
  font: 700 14px/1 var(--font-sans, sans-serif);
  text-transform: uppercase;
  letter-spacing: 0.06em;
  cursor: pointer;
  box-shadow: 0 6px 16px rgba(225, 29, 72, 0.35);
}
.drawer__cta {
  display: inline-flex; align-items: center; justify-content: center; gap: 8px;
  padding: 12px 16px;
  border-radius: 10px;
  background: rgba(37, 211, 102, 0.10);
  color: #1ea757;
  font: 700 13.5px/1 var(--font-sans, sans-serif);
  text-decoration: none;
}
.drawer__cta svg { font-size: 18px; }

@media (max-width: 540px) {
  /* Matches `#navigation { height: 70px !important }` in main.css. */
  .drawer { --drawer-top: 70px; }
}

/* Drawer transition */
.drawer-enter-active, .drawer-leave-active { transition: opacity .2s ease; }
.drawer-enter-active .drawer__nav,
.drawer-enter-active .drawer__foot,
.drawer-leave-active .drawer__nav,
.drawer-leave-active .drawer__foot { transition: transform .25s cubic-bezier(.22,1,.36,1); }
.drawer-enter-from, .drawer-leave-to { opacity: 0; }
.drawer-enter-from .drawer__nav,
.drawer-enter-from .drawer__foot,
.drawer-leave-to .drawer__nav,
.drawer-leave-to .drawer__foot { transform: translateY(-8px); }

/* ── Responsive ─────────────────────────────────────────────── */
@media (max-width: 980px) {
  .nav__primary { display: none; }
  .nav__lang { display: none; }
  .nav__login { display: none; }
  .nav__burger { display: inline-flex; }
  .nav__inner { padding: 0 20px; }
}
@media (max-width: 640px) {
  .nav__inner { padding: 0 16px; gap: 12px; }
  .nav__brand img { height: 28px; }
  .nav__user-btn { width: 34px; height: 34px; }
}
</style>
